import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ReferenceLine,
} from 'recharts';
import {
  Thermometer,
  AlertCircle,
  CheckCircle,
  WifiOff,
  Activity,
  Server,
  Gauge,
  Bell,
  MapPin,
  Cpu,
  MemoryStick,
  HardDrive,
  ExternalLink,
} from 'lucide-react';

// ---------------------------------------------------------------------------
// Configuration & helpers
// ---------------------------------------------------------------------------

// Where to reach the backend:
//  - explicit REACT_APP_API_URL wins (set at build time), else
//  - dev server on :3000 talks to the backend on :5000, else
//  - production build is served BY the backend, so use the same origin.
const API_URL =
  process.env.REACT_APP_API_URL ||
  (window.location.port === '3000'
    ? `${window.location.protocol}//${window.location.hostname}:5000`
    : window.location.origin);

const WS_URL = API_URL.replace(/^http/, 'ws') + '/ws';

const COLORS = {
  bgFrom: '#0f172a',
  bgTo: '#1e293b',
  card: '#1e293b',
  accent: '#06b6d4',
  normal: '#10b981',
  warning: '#f59e0b',
  critical: '#ef4444',
  text: '#e2e8f0',
  textDim: '#94a3b8',
  border: 'rgba(148, 163, 184, 0.15)',
};

const TIME_RANGES = [
  { label: '6h', hours: 6 },
  { label: '24h', hours: 24 },
  { label: '7d', hours: 168 },
];

function tempColor(temp, threshold) {
  if (temp == null || Number.isNaN(temp)) return COLORS.textDim;
  if (temp >= threshold) return COLORS.critical;
  if (temp >= threshold - 10) return COLORS.warning;
  return COLORS.normal;
}

function tempStatus(temp, threshold) {
  if (temp == null || Number.isNaN(temp)) return 'No data';
  if (temp >= threshold) return 'Critical';
  if (temp >= threshold - 10) return 'Warning';
  return 'Normal';
}

// Format a byte count as a human-readable string (e.g. 12.3 GB).
function formatBytes(bytes) {
  if (bytes == null || Number.isNaN(bytes)) return '—';
  const units = ['B', 'KB', 'MB', 'GB', 'TB', 'PB'];
  let v = bytes;
  let i = 0;
  while (v >= 1024 && i < units.length - 1) {
    v /= 1024;
    i++;
  }
  return `${v.toFixed(v >= 100 || i === 0 ? 0 : 1)} ${units[i]}`;
}

// Color a usage percentage: green < 70, orange 70–90, red > 90.
function usageColor(pct) {
  if (pct == null || Number.isNaN(pct)) return COLORS.textDim;
  if (pct >= 90) return COLORS.critical;
  if (pct >= 70) return COLORS.warning;
  return COLORS.normal;
}

function pct(used, total) {
  if (!total || used == null) return null;
  return Math.round((used / total) * 1000) / 10;
}

function formatUptime(seconds) {
  if (seconds == null) return '—';
  const d = Math.floor(seconds / 86400);
  const h = Math.floor((seconds % 86400) / 3600);
  if (d > 0) return `${d}d ${h}h`;
  const m = Math.floor((seconds % 3600) / 60);
  return `${h}h ${m}m`;
}

function formatTime(ts, hours) {
  const d = new Date(ts);
  if (hours > 48) {
    return d.toLocaleDateString([], { month: 'short', day: 'numeric' }) +
      ' ' + d.toLocaleTimeString([], { hour: '2-digit' });
  }
  return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}

// ---------------------------------------------------------------------------
// Styles (Tailwind-like, inline so no build step needed)
// ---------------------------------------------------------------------------

const styles = {
  page: {
    minHeight: '100vh',
    background: `linear-gradient(135deg, ${COLORS.bgFrom} 0%, ${COLORS.bgTo} 100%)`,
    color: COLORS.text,
    fontFamily:
      "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif",
    padding: '24px',
    boxSizing: 'border-box',
  },
  container: { maxWidth: '1200px', margin: '0 auto' },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: '16px',
    marginBottom: '28px',
  },
  title: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    fontSize: '26px',
    fontWeight: 700,
    margin: 0,
  },
  subtitle: { color: COLORS.textDim, fontSize: '14px', marginTop: '4px' },
  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
    gap: '16px',
    marginBottom: '24px',
  },
  card: {
    background: 'rgba(30, 41, 59, 0.7)',
    border: `1px solid ${COLORS.border}`,
    borderRadius: '16px',
    padding: '20px',
    backdropFilter: 'blur(8px)',
    transition: 'transform 0.2s ease, box-shadow 0.2s ease',
  },
  summaryLabel: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    color: COLORS.textDim,
    fontSize: '13px',
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
  },
  summaryValue: { fontSize: '32px', fontWeight: 700, marginTop: '8px' },
  sectionTitle: {
    fontSize: '18px',
    fontWeight: 600,
    margin: '8px 0 16px',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  chartCard: {
    background: 'rgba(30, 41, 59, 0.7)',
    border: `1px solid ${COLORS.border}`,
    borderRadius: '16px',
    padding: '20px',
    marginBottom: '24px',
  },
  controlsRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: '12px',
    marginBottom: '16px',
  },
  pill: (active) => ({
    padding: '6px 14px',
    borderRadius: '999px',
    border: `1px solid ${active ? COLORS.accent : COLORS.border}`,
    background: active ? 'rgba(6, 182, 212, 0.15)' : 'transparent',
    color: active ? COLORS.accent : COLORS.textDim,
    cursor: 'pointer',
    fontSize: '13px',
    fontWeight: 600,
    transition: 'all 0.15s ease',
  }),
  connBadge: (connected) => ({
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '6px 14px',
    borderRadius: '999px',
    background: connected ? 'rgba(16, 185, 129, 0.12)' : 'rgba(239, 68, 68, 0.12)',
    border: `1px solid ${connected ? COLORS.normal : COLORS.critical}`,
    color: connected ? COLORS.normal : COLORS.critical,
    fontSize: '13px',
    fontWeight: 600,
  }),
  dot: (color) => ({
    width: '9px',
    height: '9px',
    borderRadius: '50%',
    background: color,
    boxShadow: `0 0 0 0 ${color}`,
    animation: 'pulse 2s infinite',
  }),
  slider: { width: '100%', accentColor: COLORS.accent, cursor: 'pointer' },
  banner: {
    background: 'rgba(245, 158, 11, 0.12)',
    border: `1px solid ${COLORS.warning}`,
    color: COLORS.warning,
    borderRadius: '12px',
    padding: '12px 16px',
    marginBottom: '20px',
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    fontSize: '14px',
  },
};

// Inject keyframes + responsive rules once.
function useGlobalStyles() {
  useEffect(() => {
    const el = document.createElement('style');
    el.textContent = `
      @keyframes pulse {
        0%   { box-shadow: 0 0 0 0 rgba(6,182,212,0.6); }
        70%  { box-shadow: 0 0 0 8px rgba(6,182,212,0); }
        100% { box-shadow: 0 0 0 0 rgba(6,182,212,0); }
      }
      .node-card:hover { transform: translateY(-3px); box-shadow: 0 8px 24px rgba(0,0,0,0.35); }
      .tm-nodes::-webkit-scrollbar { width: 8px; }
      .tm-nodes::-webkit-scrollbar-thumb { background: rgba(148,163,184,0.3); border-radius: 8px; }

      @media (max-width: 768px) {
        .tm-page { padding: 14px !important; }
        .tm-title { font-size: 20px !important; }
        .tm-summary-value { font-size: 24px !important; }
      }

      /* --- Desktop single-screen layout (large + tall enough screens) ---
         Below this, the dashboard falls back to normal vertical scrolling. */
      @media (min-width: 1200px) and (min-height: 720px) {
        .tm-page { height: 100vh; overflow: hidden; padding: 16px 22px !important; }
        .tm-shell { height: 100%; display: flex; flex-direction: column; max-width: 1680px !important; }
        .tm-body {
          flex: 1 1 auto; min-height: 0;
          display: grid; grid-template-columns: minmax(0, 1fr) 440px; gap: 18px;
        }
        .tm-col-main { display: flex; flex-direction: column; min-height: 0; gap: 12px; }
        .tm-col-side { display: flex; flex-direction: column; min-height: 0; gap: 12px; }
        .tm-nodes { flex: 1 1 auto; min-height: 0; overflow-y: auto; padding-right: 6px; }
        .tm-chart {
          flex: 1 1 auto; min-height: 0;
          display: flex; flex-direction: column; margin-bottom: 0 !important;
        }
        .tm-chart-area { flex: 1 1 auto; min-height: 0; height: auto !important; }
        /* tighten spacing so it all fits without page scroll */
        .tm-mb { margin-bottom: 0 !important; }
        .tm-head { margin-bottom: 12px !important; }
        .tm-tabs { margin-bottom: 12px !important; }
        .tm-banner { margin-bottom: 12px !important; }
        .tm-section-title { margin: 2px 0 8px !important; font-size: 15px !important; }
        .tm-compact-card { padding: 14px !important; }
        .tm-loc-block { margin-bottom: 14px !important; }
      }
    `;
    document.head.appendChild(el);
    return () => document.head.removeChild(el);
  }, []);
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

function SummaryCard({ icon, label, value, unit, color }) {
  return (
    <div style={styles.card}>
      <div style={styles.summaryLabel}>
        {icon}
        {label}
      </div>
      <div className="tm-summary-value" style={{ ...styles.summaryValue, color: color || COLORS.text }}>
        {value}
        {unit && <span style={{ fontSize: '16px', color: COLORS.textDim, marginLeft: 4 }}>{unit}</span>}
      </div>
    </div>
  );
}

function NodeCard({ reading, threshold, selected, onSelect }) {
  const hasTemp = typeof reading.temperature === 'number';
  const color = tempColor(reading.temperature, threshold);
  const status = tempStatus(reading.temperature, threshold);
  const StatusIcon = !hasTemp
    ? WifiOff
    : status === 'Critical' || status === 'Warning'
    ? AlertCircle
    : CheckCircle;

  // For multi-server setups node names are namespaced "server/node".
  const hasServer = reading.node.includes('/');
  const serverLabel = reading.server || (hasServer ? reading.node.split('/')[0] : null);
  const displayName = hasServer ? reading.node.split('/').slice(1).join('/') : reading.node;

  return (
    <div
      className="node-card"
      onClick={() => onSelect(reading.node)}
      style={{
        ...styles.card,
        cursor: 'pointer',
        borderColor: selected ? COLORS.accent : COLORS.border,
        boxShadow: selected ? `0 0 0 1px ${COLORS.accent}` : 'none',
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontWeight: 600 }}>
          <Server size={18} color={COLORS.accent} />
          {displayName}
        </div>
        <StatusIcon size={18} color={color} />
      </div>
      {serverLabel && (
        <div
          style={{
            display: 'inline-block',
            marginTop: 8,
            padding: '2px 8px',
            borderRadius: 6,
            fontSize: 11,
            fontWeight: 600,
            color: COLORS.accent,
            background: 'rgba(6, 182, 212, 0.12)',
            border: `1px solid ${COLORS.border}`,
          }}
        >
          {serverLabel}
        </div>
      )}
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 6, marginTop: 14 }}>
        <Thermometer size={26} color={color} />
        {hasTemp ? (
          <>
            <span style={{ fontSize: 38, fontWeight: 700, color }}>
              {reading.temperature.toFixed(1)}
            </span>
            <span style={{ fontSize: 18, color: COLORS.textDim }}>°C</span>
          </>
        ) : (
          <span style={{ fontSize: 22, fontWeight: 600, color: COLORS.textDim }}>No data</span>
        )}
      </div>
      <div style={{ marginTop: 10, fontSize: 13, color }}>
        {status}
        <span style={{ color: COLORS.textDim, marginLeft: 8 }}>
          {new Date(reading.timestamp).toLocaleTimeString()}
        </span>
      </div>
    </div>
  );
}

function ChartTooltip({ active, payload, label, hours }) {
  if (!active || !payload || !payload.length) return null;
  return (
    <div
      style={{
        background: COLORS.bgFrom,
        border: `1px solid ${COLORS.border}`,
        borderRadius: 8,
        padding: '8px 12px',
        fontSize: 13,
      }}
    >
      <div style={{ color: COLORS.textDim, marginBottom: 4 }}>{formatTime(label, hours)}</div>
      <div style={{ color: COLORS.accent, fontWeight: 600 }}>
        {payload[0].value.toFixed(1)} °C
      </div>
    </div>
  );
}

// A labelled usage bar (CPU / memory / disk).
function UsageBar({ icon, label, percent, detail }) {
  const color = usageColor(percent);
  const width = percent == null ? 0 : Math.min(100, Math.max(0, percent));
  return (
    <div style={{ marginTop: 12 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, marginBottom: 4 }}>
        <span style={{ display: 'flex', alignItems: 'center', gap: 6, color: COLORS.textDim }}>
          {icon}
          {label}
        </span>
        <span style={{ color, fontWeight: 600 }}>
          {percent == null ? '—' : `${percent.toFixed(0)}%`}
        </span>
      </div>
      <div style={{ height: 8, background: 'rgba(148,163,184,0.15)', borderRadius: 6, overflow: 'hidden' }}>
        <div
          style={{
            width: `${width}%`,
            height: '100%',
            background: color,
            borderRadius: 6,
            transition: 'width 0.4s ease',
          }}
        />
      </div>
      {detail && <div style={{ fontSize: 11, color: COLORS.textDim, marginTop: 3 }}>{detail}</div>}
    </div>
  );
}

function ResourceCard({ reading, selected, onSelect }) {
  const hasServer = reading.node.includes('/');
  const serverLabel = reading.server || (hasServer ? reading.node.split('/')[0] : null);
  const displayName = hasServer ? reading.node.split('/').slice(1).join('/') : reading.node;

  const memPct = pct(reading.memUsed, reading.memTotal);
  const diskPct = pct(reading.diskUsed, reading.diskTotal);

  const handleClick = () => {
    onSelect(reading.node); // keep the history chart in sync
    if (reading.url) {
      // Open the node's Proxmox web UI in a new tab (dashboard stays open).
      window.open(reading.url, '_blank', 'noopener,noreferrer');
    }
  };

  return (
    <div
      className="node-card"
      onClick={handleClick}
      title={reading.url ? `Open ${displayName} in Proxmox` : displayName}
      style={{
        ...styles.card,
        cursor: 'pointer',
        borderColor: selected ? COLORS.accent : COLORS.border,
        boxShadow: selected ? `0 0 0 1px ${COLORS.accent}` : 'none',
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontWeight: 600 }}>
          <Server size={18} color={COLORS.accent} />
          {displayName}
          {reading.url && <ExternalLink size={13} color={COLORS.textDim} />}
        </div>
        <span style={{ fontSize: 11, color: COLORS.textDim }}>{formatUptime(reading.uptime)}</span>
      </div>
      {serverLabel && (
        <div
          style={{
            display: 'inline-block',
            marginTop: 8,
            padding: '2px 8px',
            borderRadius: 6,
            fontSize: 11,
            fontWeight: 600,
            color: COLORS.accent,
            background: 'rgba(6, 182, 212, 0.12)',
            border: `1px solid ${COLORS.border}`,
          }}
        >
          {serverLabel}
        </div>
      )}
      <UsageBar
        icon={<Cpu size={13} />}
        label={`CPU${reading.cpuCount ? ` · ${reading.cpuCount} cores` : ''}`}
        percent={reading.cpu}
      />
      <UsageBar
        icon={<MemoryStick size={13} />}
        label="Memory"
        percent={memPct}
        detail={`${formatBytes(reading.memUsed)} / ${formatBytes(reading.memTotal)}`}
      />
      <UsageBar
        icon={<HardDrive size={13} />}
        label="Disk (root)"
        percent={diskPct}
        detail={`${formatBytes(reading.diskUsed)} / ${formatBytes(reading.diskTotal)}`}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export default function TemperatureDashboard() {
  useGlobalStyles();

  const [view, setView] = useState('temperature'); // 'temperature' | 'resources'
  const [temperatures, setTemperatures] = useState([]); // current readings
  const [resources, setResources] = useState([]); // current CPU/RAM/disk
  const [meta, setMeta] = useState({});
  const [connected, setConnected] = useState(false);
  const [threshold, setThreshold] = useState(75);
  const [selectedNode, setSelectedNode] = useState(null);
  const [timeRange, setTimeRange] = useState(TIME_RANGES[1]); // 24h
  const [history, setHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [resourceHistory, setResourceHistory] = useState([]);

  const wsRef = useRef(null);
  const reconnectRef = useRef(null);
  const selectedNodeRef = useRef(null);

  // Keep a ref in sync so the WS handler can auto-select the first node.
  selectedNodeRef.current = selectedNode;

  // ---- WebSocket connection with auto-reconnect ----------------------------
  const connect = useCallback(() => {
    try {
      const ws = new WebSocket(WS_URL);
      wsRef.current = ws;

      ws.onopen = () => {
        setConnected(true);
        if (reconnectRef.current) {
          clearTimeout(reconnectRef.current);
          reconnectRef.current = null;
        }
      };

      ws.onmessage = (event) => {
        try {
          const msg = JSON.parse(event.data);
          if (msg.type === 'temperature_update') {
            setTemperatures(msg.data || []);
            if (msg.resources) setResources(msg.resources);
            setMeta(msg.meta || {});
            if (!selectedNodeRef.current && msg.data && msg.data.length) {
              setSelectedNode(msg.data[0].node);
            }
          }
        } catch (e) {
          /* ignore malformed messages */
        }
      };

      ws.onclose = () => {
        setConnected(false);
        // Auto-reconnect after 3s.
        if (!reconnectRef.current) {
          reconnectRef.current = setTimeout(() => {
            reconnectRef.current = null;
            connect();
          }, 3000);
        }
      };

      ws.onerror = () => ws.close();
    } catch (e) {
      setConnected(false);
    }
  }, []);

  useEffect(() => {
    connect();
    return () => {
      if (reconnectRef.current) clearTimeout(reconnectRef.current);
      if (wsRef.current) wsRef.current.close();
    };
  }, [connect]);

  // ---- Fallback: also fetch current temps + resources via REST on mount ----
  useEffect(() => {
    fetch(`${API_URL}/api/temperatures`)
      .then((r) => r.json())
      .then((json) => {
        if (json.data && json.data.length) {
          setTemperatures(json.data);
          setMeta(json.meta || {});
          if (!selectedNodeRef.current) setSelectedNode(json.data[0].node);
        }
      })
      .catch(() => {});
    fetch(`${API_URL}/api/resources`)
      .then((r) => r.json())
      .then((json) => {
        if (json.data) setResources(json.data);
      })
      .catch(() => {});
  }, []);

  // ---- Load history when node / range changes ------------------------------
  const loadHistory = useCallback(() => {
    if (!selectedNode) return;
    setLoadingHistory(true);
    fetch(`${API_URL}/api/history/${encodeURIComponent(selectedNode)}?hours=${timeRange.hours}`)
      .then((r) => r.json())
      .then((json) => {
        const data = (json.data || []).map((row) => ({
          timestamp: row.timestamp,
          temperature: row.temperature,
        }));
        setHistory(data);
      })
      .catch(() => setHistory([]))
      .finally(() => setLoadingHistory(false));
  }, [selectedNode, timeRange]);

  useEffect(() => {
    loadHistory();
  }, [loadHistory]);

  // Refresh history alongside live updates (keeps chart current).
  useEffect(() => {
    if (!selectedNode) return;
    const id = setInterval(loadHistory, 30000);
    return () => clearInterval(id);
  }, [selectedNode, loadHistory]);

  // ---- Load resource history (resources view) ------------------------------
  const loadResourceHistory = useCallback(() => {
    if (!selectedNode) return;
    fetch(`${API_URL}/api/resources/history/${encodeURIComponent(selectedNode)}?hours=${timeRange.hours}`)
      .then((r) => r.json())
      .then((json) => {
        const data = (json.data || []).map((row) => ({
          timestamp: row.timestamp,
          cpu: row.cpu,
          memPct: row.mem_total ? Math.round((row.mem_used / row.mem_total) * 1000) / 10 : null,
          diskPct: row.disk_total ? Math.round((row.disk_used / row.disk_total) * 1000) / 10 : null,
        }));
        setResourceHistory(data);
      })
      .catch(() => setResourceHistory([]));
  }, [selectedNode, timeRange]);

  useEffect(() => {
    if (view === 'resources') loadResourceHistory();
  }, [view, loadResourceHistory]);

  useEffect(() => {
    if (view !== 'resources' || !selectedNode) return;
    const id = setInterval(loadResourceHistory, 30000);
    return () => clearInterval(id);
  }, [view, selectedNode, loadResourceHistory]);

  // ---- Derived summary values ----------------------------------------------
  const { avgTemp, maxTemp, alertCount, hasReadings } = useMemo(() => {
    // Only consider nodes that actually report a numeric temperature.
    const temps = temperatures
      .map((t) => t.temperature)
      .filter((t) => typeof t === 'number' && !Number.isNaN(t));
    if (!temps.length) return { avgTemp: 0, maxTemp: 0, alertCount: 0, hasReadings: false };
    return {
      avgTemp: temps.reduce((a, b) => a + b, 0) / temps.length,
      maxTemp: Math.max(...temps),
      alertCount: temps.filter((t) => t >= threshold).length,
      hasReadings: true,
    };
  }, [temperatures, threshold]);

  // Group nodes by location for the dashboard sections.
  const { locationGroups, showLocations } = useMemo(() => {
    const map = new Map();
    for (const reading of temperatures) {
      const loc = (reading.location && reading.location.trim()) || 'Unspecified';
      if (!map.has(loc)) map.set(loc, []);
      map.get(loc).push(reading);
    }
    const groups = [...map.entries()]
      .map(([location, nodes]) => ({
        location,
        nodes,
        alertCount: nodes.filter(
          (n) => typeof n.temperature === 'number' && n.temperature >= threshold
        ).length,
      }))
      // Sort: named locations alphabetically, "Unspecified" last.
      .sort((a, b) => {
        if (a.location === 'Unspecified') return 1;
        if (b.location === 'Unspecified') return -1;
        return a.location.localeCompare(b.location);
      });
    // Only show location headers if there's real grouping to show.
    const namedLocations = groups.filter((g) => g.location !== 'Unspecified');
    const show = namedLocations.length > 0 && groups.length > 1;
    return { locationGroups: groups, showLocations: show };
  }, [temperatures, threshold]);

  // Fleet-wide resource averages for the summary cards.
  const resourceSummary = useMemo(() => {
    const avg = (arr) =>
      arr.length ? Math.round((arr.reduce((a, b) => a + b, 0) / arr.length) * 10) / 10 : null;
    const cpus = resources.map((r) => r.cpu).filter((v) => typeof v === 'number');
    const mems = resources.map((r) => pct(r.memUsed, r.memTotal)).filter((v) => v != null);
    const disks = resources.map((r) => pct(r.diskUsed, r.diskTotal)).filter((v) => v != null);
    return { avgCpu: avg(cpus), avgMem: avg(mems), avgDisk: avg(disks), count: resources.length };
  }, [resources]);

  // Group resource nodes by location the same way.
  const resourceGroups = useMemo(() => {
    const map = new Map();
    for (const r of resources) {
      const loc = (r.location && r.location.trim()) || 'Unspecified';
      if (!map.has(loc)) map.set(loc, []);
      map.get(loc).push(r);
    }
    return [...map.entries()]
      .map(([location, nodes]) => ({ location, nodes }))
      .sort((a, b) => {
        if (a.location === 'Unspecified') return 1;
        if (b.location === 'Unspecified') return -1;
        return a.location.localeCompare(b.location);
      });
  }, [resources]);

  const usingMock = meta.usingMockData;

  return (
    <div className="tm-page" style={styles.page}>
      <div className="tm-shell" style={styles.container}>
        {/* Header */}
        <header className="tm-head" style={styles.header}>
          <div>
            <h1 className="tm-title" style={styles.title}>
              <Server size={30} color={COLORS.accent} />
              Proxmox Monitor
            </h1>
            <div style={styles.subtitle}>
              Real-time monitoring · updates every{' '}
              {Math.round((meta.pollInterval || 30000) / 1000)}s
              {meta.serverCount > 1 && ` · ${meta.serverCount} servers`}
            </div>
          </div>
          <div style={styles.connBadge(connected)}>
            {connected ? (
              <>
                <span style={styles.dot(COLORS.normal)} />
                <Activity size={15} /> Live
              </>
            ) : (
              <>
                <WifiOff size={15} /> Disconnected
              </>
            )}
          </div>
        </header>

        {/* View tabs */}
        <div className="tm-tabs" style={{ display: 'flex', gap: 8, marginBottom: 20 }}>
          {[
            { key: 'temperature', label: 'Temperature', icon: <Thermometer size={16} /> },
            { key: 'resources', label: 'Resources', icon: <Cpu size={16} /> },
          ].map((tab) => (
            <button
              key={tab.key}
              onClick={() => setView(tab.key)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                padding: '10px 18px',
                borderRadius: 10,
                border: `1px solid ${view === tab.key ? COLORS.accent : COLORS.border}`,
                background: view === tab.key ? 'rgba(6, 182, 212, 0.15)' : 'transparent',
                color: view === tab.key ? COLORS.accent : COLORS.textDim,
                cursor: 'pointer',
                fontSize: 14,
                fontWeight: 600,
                transition: 'all 0.15s ease',
              }}
            >
              {tab.icon}
              {tab.label}
            </button>
          ))}
        </div>

        {usingMock && (
          <div className="tm-banner" style={styles.banner}>
            <AlertCircle size={18} />
            Showing simulated data — real Proxmox sensor data is unavailable. See README to
            connect real hardware sensors.
          </div>
        )}

        {view === 'temperature' && (
        <div className="tm-body">
        <div className="tm-col-main">
        {/* Summary cards */}
        <div className="tm-summary tm-mb" style={styles.grid}>
          <SummaryCard
            icon={<Server size={15} />}
            label="Total Nodes"
            value={temperatures.length}
          />
          <SummaryCard
            icon={<Gauge size={15} />}
            label="Average Temp"
            value={hasReadings ? avgTemp.toFixed(1) : '—'}
            unit={hasReadings ? '°C' : ''}
            color={hasReadings ? tempColor(avgTemp, threshold) : COLORS.textDim}
          />
          <SummaryCard
            icon={<Thermometer size={15} />}
            label="Max Temp"
            value={hasReadings ? maxTemp.toFixed(1) : '—'}
            unit={hasReadings ? '°C' : ''}
            color={hasReadings ? tempColor(maxTemp, threshold) : COLORS.textDim}
          />
          <SummaryCard
            icon={<Bell size={15} />}
            label="Alert Threshold"
            value={threshold}
            unit="°C"
            color={alertCount > 0 ? COLORS.critical : COLORS.text}
          />
        </div>

        {/* Alert threshold slider */}
        <div className="tm-mb tm-compact-card" style={{ ...styles.chartCard, paddingTop: 16, paddingBottom: 16 }}>
          <div style={styles.controlsRow}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontWeight: 600 }}>
              <Bell size={16} color={COLORS.accent} />
              Alert Threshold
              {alertCount > 0 && (
                <span style={{ color: COLORS.critical, fontSize: 13 }}>
                  · {alertCount} node{alertCount > 1 ? 's' : ''} in alert
                </span>
              )}
            </div>
            <span style={{ color: COLORS.accent, fontWeight: 700, fontSize: 18 }}>
              {threshold}°C
            </span>
          </div>
          <input
            type="range"
            min={40}
            max={100}
            value={threshold}
            onChange={(e) => setThreshold(parseInt(e.target.value, 10))}
            style={styles.slider}
          />
          <div style={{ display: 'flex', justifyContent: 'space-between', color: COLORS.textDim, fontSize: 12 }}>
            <span>40°C</span>
            <span>100°C</span>
          </div>
        </div>

        {/* Node cards, grouped by location */}
        <h2 className="tm-section-title" style={styles.sectionTitle}>
          <Server size={18} color={COLORS.accent} /> Nodes
        </h2>
        <div className="tm-nodes">
        {temperatures.length === 0 ? (
          <div style={{ ...styles.card, color: COLORS.textDim }}>
            No temperature data yet. Waiting for the first poll…
          </div>
        ) : (
          locationGroups.map(({ location, nodes, alertCount: groupAlerts }) => (
            <div key={location} className="tm-loc-block" style={{ marginBottom: 24 }}>
              {/* Location subheader (hidden if only one, unnamed group) */}
              {showLocations && (
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                    margin: '4px 0 12px',
                    color: COLORS.textDim,
                    fontSize: 14,
                    fontWeight: 600,
                  }}
                >
                  <MapPin size={16} color={COLORS.accent} />
                  <span style={{ color: COLORS.text }}>{location}</span>
                  <span style={{ fontSize: 12 }}>
                    · {nodes.length} node{nodes.length > 1 ? 's' : ''}
                  </span>
                  {groupAlerts > 0 && (
                    <span style={{ fontSize: 12, color: COLORS.critical }}>
                      · {groupAlerts} in alert
                    </span>
                  )}
                </div>
              )}
              <div style={styles.grid}>
                {nodes.map((reading) => (
                  <NodeCard
                    key={reading.node}
                    reading={reading}
                    threshold={threshold}
                    selected={selectedNode === reading.node}
                    onSelect={setSelectedNode}
                  />
                ))}
              </div>
            </div>
          ))
        )}
        </div>{/* tm-nodes */}
        </div>{/* tm-col-main */}

        <div className="tm-col-side">
        {/* History chart */}
        <div className="tm-chart" style={styles.chartCard}>
          <div style={styles.controlsRow}>
            <h2 className="tm-section-title" style={{ ...styles.sectionTitle, margin: 0 }}>
              <Activity size={18} color={COLORS.accent} />
              History{selectedNode ? ` · ${selectedNode}` : ''}
            </h2>
            <div style={{ display: 'flex', gap: 8 }}>
              {TIME_RANGES.map((range) => (
                <button
                  key={range.label}
                  style={styles.pill(timeRange.label === range.label)}
                  onClick={() => setTimeRange(range)}
                >
                  {range.label}
                </button>
              ))}
            </div>
          </div>

          <div className="tm-chart-area" style={{ width: '100%', height: 320 }}>
            {loadingHistory && history.length === 0 ? (
              <div style={{ color: COLORS.textDim, padding: 40, textAlign: 'center' }}>
                Loading history…
              </div>
            ) : history.length === 0 ? (
              <div style={{ color: COLORS.textDim, padding: 40, textAlign: 'center' }}>
                No history for this range yet.
              </div>
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={history} margin={{ top: 10, right: 16, left: -8, bottom: 0 }}>
                  <defs>
                    <linearGradient id="tempGradient" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor={COLORS.accent} stopOpacity={0.8} />
                      <stop offset="100%" stopColor={COLORS.accent} stopOpacity={0.1} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke={COLORS.border} />
                  <XAxis
                    dataKey="timestamp"
                    tickFormatter={(t) => formatTime(t, timeRange.hours)}
                    stroke={COLORS.textDim}
                    fontSize={12}
                    minTickGap={40}
                  />
                  <YAxis
                    stroke={COLORS.textDim}
                    fontSize={12}
                    domain={['dataMin - 5', 'dataMax + 5']}
                    tickFormatter={(v) => `${Math.round(v)}°`}
                  />
                  <Tooltip content={<ChartTooltip hours={timeRange.hours} />} />
                  <ReferenceLine
                    y={threshold}
                    stroke={COLORS.critical}
                    strokeDasharray="4 4"
                    label={{ value: `Alert ${threshold}°C`, fill: COLORS.critical, fontSize: 11, position: 'insideTopRight' }}
                  />
                  <Line
                    type="monotone"
                    dataKey="temperature"
                    stroke={COLORS.accent}
                    strokeWidth={2}
                    dot={false}
                    activeDot={{ r: 5, fill: COLORS.accent }}
                    isAnimationActive={false}
                  />
                </LineChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>
        </div>{/* tm-col-side */}
        </div>
        )}

        {view === 'resources' && (
        <div className="tm-body">
        <div className="tm-col-main">
        {/* Resource summary cards */}
        <div className="tm-summary tm-mb" style={styles.grid}>
          <SummaryCard icon={<Server size={15} />} label="Total Nodes" value={resourceSummary.count} />
          <SummaryCard
            icon={<Cpu size={15} />}
            label="Avg CPU"
            value={resourceSummary.avgCpu == null ? '—' : resourceSummary.avgCpu.toFixed(0)}
            unit={resourceSummary.avgCpu == null ? '' : '%'}
            color={usageColor(resourceSummary.avgCpu)}
          />
          <SummaryCard
            icon={<MemoryStick size={15} />}
            label="Avg Memory"
            value={resourceSummary.avgMem == null ? '—' : resourceSummary.avgMem.toFixed(0)}
            unit={resourceSummary.avgMem == null ? '' : '%'}
            color={usageColor(resourceSummary.avgMem)}
          />
          <SummaryCard
            icon={<HardDrive size={15} />}
            label="Avg Disk"
            value={resourceSummary.avgDisk == null ? '—' : resourceSummary.avgDisk.toFixed(0)}
            unit={resourceSummary.avgDisk == null ? '' : '%'}
            color={usageColor(resourceSummary.avgDisk)}
          />
        </div>

        {/* Resource node cards, grouped by location */}
        <h2 className="tm-section-title" style={styles.sectionTitle}>
          <Cpu size={18} color={COLORS.accent} /> Resource Usage
        </h2>
        <div className="tm-nodes">
        {resources.length === 0 ? (
          <div style={{ ...styles.card, color: COLORS.textDim }}>
            No resource data yet. Waiting for the first poll…
          </div>
        ) : (
          resourceGroups.map(({ location, nodes }) => (
            <div key={location} className="tm-loc-block" style={{ marginBottom: 24 }}>
              {showLocations && (
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                    margin: '4px 0 12px',
                    fontSize: 14,
                    fontWeight: 600,
                  }}
                >
                  <MapPin size={16} color={COLORS.accent} />
                  <span style={{ color: COLORS.text }}>{location}</span>
                  <span style={{ fontSize: 12, color: COLORS.textDim }}>
                    · {nodes.length} node{nodes.length > 1 ? 's' : ''}
                  </span>
                </div>
              )}
              <div style={styles.grid}>
                {nodes.map((r) => (
                  <ResourceCard
                    key={r.node}
                    reading={r}
                    selected={selectedNode === r.node}
                    onSelect={setSelectedNode}
                  />
                ))}
              </div>
            </div>
          ))
        )}
        </div>{/* tm-nodes */}
        </div>{/* tm-col-main */}

        <div className="tm-col-side">
        {/* Resource history chart */}
        <div className="tm-chart" style={styles.chartCard}>
          <div style={styles.controlsRow}>
            <h2 className="tm-section-title" style={{ ...styles.sectionTitle, margin: 0 }}>
              <Activity size={18} color={COLORS.accent} />
              Usage History{selectedNode ? ` · ${selectedNode}` : ''}
            </h2>
            <div style={{ display: 'flex', gap: 8 }}>
              {TIME_RANGES.map((range) => (
                <button
                  key={range.label}
                  style={styles.pill(timeRange.label === range.label)}
                  onClick={() => setTimeRange(range)}
                >
                  {range.label}
                </button>
              ))}
            </div>
          </div>
          <div className="tm-chart-area" style={{ width: '100%', height: 320 }}>
            {resourceHistory.length === 0 ? (
              <div style={{ color: COLORS.textDim, padding: 40, textAlign: 'center' }}>
                No usage history for this range yet.
              </div>
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={resourceHistory} margin={{ top: 10, right: 16, left: -8, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke={COLORS.border} />
                  <XAxis
                    dataKey="timestamp"
                    tickFormatter={(t) => formatTime(t, timeRange.hours)}
                    stroke={COLORS.textDim}
                    fontSize={12}
                    minTickGap={40}
                  />
                  <YAxis
                    stroke={COLORS.textDim}
                    fontSize={12}
                    domain={[0, 100]}
                    tickFormatter={(v) => `${v}%`}
                  />
                  <Tooltip
                    contentStyle={{
                      background: COLORS.bgFrom,
                      border: `1px solid ${COLORS.border}`,
                      borderRadius: 8,
                      fontSize: 13,
                    }}
                    labelFormatter={(t) => formatTime(t, timeRange.hours)}
                    formatter={(v, name) => [`${v == null ? '—' : v.toFixed(1)}%`, name]}
                  />
                  <Line type="monotone" dataKey="cpu" name="CPU" stroke={COLORS.accent} strokeWidth={2} dot={false} isAnimationActive={false} />
                  <Line type="monotone" dataKey="memPct" name="Memory" stroke={COLORS.warning} strokeWidth={2} dot={false} isAnimationActive={false} />
                  <Line type="monotone" dataKey="diskPct" name="Disk" stroke={COLORS.normal} strokeWidth={2} dot={false} isAnimationActive={false} />
                </LineChart>
              </ResponsiveContainer>
            )}
          </div>
          {resourceHistory.length > 0 && (
            <div style={{ display: 'flex', gap: 16, justifyContent: 'center', marginTop: 8, fontSize: 12 }}>
              <span style={{ color: COLORS.accent }}>● CPU</span>
              <span style={{ color: COLORS.warning }}>● Memory</span>
              <span style={{ color: COLORS.normal }}>● Disk</span>
            </div>
          )}
        </div>
        </div>{/* tm-col-side */}
        </div>
        )}

        <footer style={{ textAlign: 'center', color: COLORS.textDim, fontSize: 12, marginTop: 8 }}>
          {meta.lastPollAt && <>Last poll: {new Date(meta.lastPollAt).toLocaleString()} · </>}
          {meta.proxmoxConnected ? 'Proxmox connected' : 'Proxmox offline'}
        </footer>
      </div>
    </div>
  );
}
